import { MonitorUp, Pin, PinOff, Maximize, Minimize } from 'lucide-react';

import { useEffect, useRef } from 'react';
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';

import { DOUBLE_TAP_MS, useSurfaceFullscreen } from './fullscreen';
import { safePlay } from './media';
import type { ScreenShareTile } from './roomState';

interface ScreenTileProps {
  readonly share: ScreenShareTile;
  /**
   * Fill mode (the share stage): the tile fills its parent box and drops its own frame
   * (the stage surface carries ring/backdrop), while the video keeps object-contain so
   * screen text never distorts. Default is the standalone aspect-video card.
   */
  readonly fill?: boolean;
  /** True while this share tile is the explicitly pinned tile. */
  readonly pinned?: boolean;
  /** Provided when the tile should offer the pin/unpin affordance (click-to-pin surface). */
  readonly onTogglePin?: (() => void) | undefined;
}

/**
 * One screen-share tile in the Verba look: attaches the participant's screen video track to a
 * muted <video> element (object-contain keeps text and UI geometry undistorted) with a chip
 * caption saying whose screen is being viewed. The element is laid out at its real rendered
 * size before attach, so adaptiveStream sizes the subscribed layer to what the UI shows.
 *
 * Every tile carries the fullscreen affordance (the mobile complaint: a shared screen had no
 * way to expand): the always-visible toggle button next to the pin, plus a double-tap on the
 * video as the touch shortcut. Desktop takes the standard element path; iPhone Safari, which
 * cannot fullscreen a non-video element, falls back to the native video player; engines with
 * neither get a short honest hint instead of a dead control (fullscreen.ts).
 */
export function ScreenTile({
  share,
  fill = false,
  pinned = false,
  onTogglePin,
}: ScreenTileProps): JSX.Element {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const figureRef = useRef<HTMLElement | null>(null);
  const track = share.track;
  const fullscreen = useSurfaceFullscreen(figureRef, videoRef);
  const lastTapRef = useRef(0);

  useEffect(() => {
    const element = videoRef.current;
    if (element === null) {
      return;
    }
    track.attach(element);
    safePlay(element);
    return () => {
      track.detach(element);
    };
  }, [track]);

  /** Double-tap on the video toggles fullscreen; a single tap keeps bubbling to the pin. */
  const onVideoTap = (): void => {
    const now = Date.now();
    if (now - lastTapRef.current <= DOUBLE_TAP_MS) {
      lastTapRef.current = 0;
      fullscreen.toggle();
      return;
    }
    lastTapRef.current = now;
  };

  return (
    <figure
      ref={figureRef}
      data-testid={`screen-tile-${share.identity}`}
      aria-label={
        share.isLocal
          ? t('room.screen_aria_own')
          : t('room.screen_aria_other', { label: share.label })
      }
      className={cn(
        'vc-fullscreen-surface group relative min-h-0 overflow-hidden',
        fill ? 'h-full w-full' : 'aspect-video',
        fill
          ? undefined
          : 'rounded-xl border border-white/10 bg-zinc-950 shadow-xl shadow-black/40',
        fullscreen.active && 'bg-black',
      )}
    >
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        onClick={onVideoTap}
        className="h-full w-full object-contain"
      />
      <div className="absolute left-3 top-3 flex items-center gap-2">
        <span className="flex items-center gap-1 rounded-md bg-emerald-500 px-1.5 py-0.5 text-[10px] font-bold uppercase text-zinc-950">
          <MonitorUp className="h-3 w-3" />
          {t('room.sharing_indicator')}
        </span>
        <span className="rounded-md bg-black/50 px-2 py-0.5 text-[11px] font-medium text-white backdrop-blur">
          {share.isLocal ? t('room.screen_own') : t('room.screen_other', { label: share.label })}
        </span>
      </div>
      {/* The fullscreen toggle is ALWAYS visible (mobile has no hover to reveal controls) and
          sits left of the pin button; on a pressed state it offers the way back out. */}
      <button
        type="button"
        data-testid={`screen-fullscreen-${share.identity}`}
        aria-pressed={fullscreen.active}
        aria-label={fullscreen.active ? t('controls.exit_fullscreen') : t('controls.fullscreen')}
        title={fullscreen.active ? t('controls.exit_fullscreen') : t('controls.fullscreen')}
        onClick={fullscreen.toggle}
        className={cn(
          'absolute top-2 z-20 grid h-7 w-7 place-items-center rounded-md bg-black/55 text-white backdrop-blur-sm transition-colors hover:bg-black/70',
          onTogglePin !== undefined ? 'right-11' : 'right-2',
          fullscreen.active && 'text-emerald-300',
        )}
      >
        {fullscreen.active ? <Minimize className="size-3.5" /> : <Maximize className="size-3.5" />}
        <span className="sr-only">
          {fullscreen.active ? t('controls.exit_fullscreen') : t('controls.fullscreen')}
        </span>
      </button>
      {onTogglePin !== undefined ? (
        <button
          type="button"
          data-testid={`pin-screen-${share.identity}`}
          onClick={onTogglePin}
          title={pinned ? t('participants.unpin') : t('participants.pin')}
          aria-label={pinned ? t('participants.unpin') : t('participants.pin')}
          className={cn(
            'absolute right-2 top-2 z-20 grid h-7 w-7 place-items-center rounded-md bg-black/55 text-white backdrop-blur-sm transition-colors hover:bg-black/70',
            pinned ? 'text-emerald-300' : 'opacity-0 group-hover:opacity-100 focus:opacity-100',
          )}
        >
          {pinned ? <PinOff className="size-3.5" /> : <Pin className="size-3.5" />}
          <span className="sr-only">
            {pinned ? t('participants.unpin_short') : t('participants.pin_short')}
          </span>
        </button>
      ) : null}
      {fullscreen.unavailable ? (
        <p
          role="status"
          data-testid="screen-fullscreen-hint"
          className="absolute left-1/2 top-3 z-30 -translate-x-1/2 rounded-md bg-black/60 px-2.5 py-1 text-[11px] font-medium text-zinc-100 backdrop-blur"
        >
          {t('room.fullscreen_hint')}
        </p>
      ) : null}
    </figure>
  );
}
